import { computed, onMounted, Ref, ref } from 'vue'
import { AutoCompleteManager } from './AutoComplete'
import { usePejerBase } from './stores/pejerData'
import { JSSchemaObject, JSSchemaObjectTypes } from './tables'
import { PejerTableNames, PejerTablesList } from './types/base'
import {
  FilterOperators,
  FilterOperatorsList,
  SearchFunctionArgs,
  SearchQueryKeys,
  type OperatorObject,
  type SearchQuery,
} from './types/types'
import useQueryParams from './useQueryParams'
import { parseSearchArgsFromString, stringifySearchArgs } from './utils'

export type SearchFunction<T> = (query: SearchFunctionArgs<T>['query']) => void

export type SearchableColumn<ITEM extends object> = {
  // key is connected to the table, so if the table parameter is provided,
  // the key will be used to get the columns from that table
  key: SearchQueryKeys<ITEM>
  label: string
  queryParam?: string
  table?: PejerTableNames
  makeMiddleNamesOptional?: boolean
  type?: 'string' | 'number' | 'date' | 'boolean'
}

// IS THIS OBSOLETE? all nested objects are already flattened
const flattenQueryObject = <T>(query: SearchQuery<T>, prefix = ''): SearchQuery<T> => {
  // Flatten the query from a deep object to a flat object
  return Object.keys(query).reduce((acc, key) => {
    const pre = prefix.length ? prefix + '.' : ''
    if (FilterOperatorsList.includes(key as FilterOperators)) {
      acc = { ...acc, [prefix]: query }
      return acc
    }
    if (typeof query[key] === 'object' && query[key] !== null) {
      acc = {
        ...acc,
        ...flattenQueryObject(query[key] as SearchQuery<T>, pre + key),
      }
    } else {
      return { ...acc, [pre + key]: query }
    }
    return acc
  }, {} as SearchQuery<T>)
}
export default function useSearch<
  TABLE extends PejerTableNames,
  ITEM extends PejerTablesList<TABLE>,
>(
  table: TABLE,
  searchableColumns: SearchableColumn<ITEM>[] | undefined,
  search: SearchFunction<ITEM>,
  staticFilters?: Ref<SearchQuery<ITEM>>,
  options?: {
    autoCompleteManager: AutoCompleteManager
    key?: string
    initialSearch?: SearchQuery<ITEM>
  }
) {
  const flattenedStaticFilters = computed(() => {
    return staticFilters.value ? flattenQueryObject(staticFilters.value) : {}
  })

  const { state, updateParams } = useQueryParams<SearchQuery<ITEM>>(
    options.initialSearch ?? {},
    'filters',
    {
      formatter: {
        stringify: (value) => stringifySearchArgs(value),
        parse: (value) => parseSearchArgsFromString(value),
      },
      key: options?.key,
    }
  )

  const store = usePejerBase()
  const schema = store.getListSchema(table)
  const schemaFields = store.flattenSchemaFields(schema)

  const filterQuery = ref<SearchQuery<ITEM>>(state.value)
  const autoCompleteQuery = ref<string>()
  const autoComplete = ref<string[]>()

  function calculateAutocomplete(text: string) {
    const autoCompleteTable = getAutoCompleteTable(activeColumn.value)

    const autoCompleteCol = options?.autoCompleteManager.getColumn(
      AutoCompleteManager.formatKey(
        autoCompleteTable.autoCompleteTable,
        autoCompleteTable.relevantColumn as string
      )
    )

    if (!autoCompleteCol) return

    const suggestions = autoCompleteCol.getSuggestions(text, 5)
    autoComplete.value = suggestions
  }
  const autoCompleteLoading = ref(false)

  onMounted(() => {
    if (!searchableColumns) return
    for (const col of searchableColumns) {
      if (!(col.key in schemaFields)) {
        throw new Error(`Column ${String(col.key)} not found in schema: ${schema.name}`)
      }
      const colName =
        'relation' in schemaFields[col.key as string] && schemaFields[col.key as string].relation
          ? schemaFields[col.key as string].relation
          : AutoCompleteManager.formatKey(col.table || table, col.key as string)

      options?.autoCompleteManager?.addColumn(
        colName
        // AutoCompleteManager.formatKey(col.table || table, col.key as string)
      )
    }
  })

  function getAutoCompleteTable(column: string) {
    const schemaColumn = store.getSchemaObjectFromFlattenedKeys(schema, column)

    if (!('relation' in schemaColumn)) {
      return {
        autoCompleteTable: table,
        relevantColumn: column,
      }
    }
    const autoCompleteTable = schemaColumn.relation?.split('.')[0] as TABLE
    const periodIndex = schemaColumn.relation?.indexOf('.')
    const relevantColumn = (
      periodIndex && periodIndex !== -1 ? schemaColumn.relation?.slice(periodIndex + 1) : column
    ) as SearchQueryKeys<ITEM>

    return {
      autoCompleteTable,
      relevantColumn,
    }
  }

  // Populates the autocomplete list if the user has typed more than 3 characters
  function updateAutoCompleteQuery(text: string) {
    return
    const activeColumnValue = activeColumn.value //as SearchQueryKeys<ITEM>
    const filterQueryValue = filterQuery.value as SearchQuery<ITEM>

    const autoCompleteTable = getAutoCompleteTable(activeColumnValue as string)

    const relevantColumn = autoCompleteTable.relevantColumn as SearchQueryKeys<ITEM>
    const relevantQuery = {} as SearchQuery<ITEM>

    // Just to make it update in realtime
    if (!filterQueryValue[activeColumnValue] || !filterQueryValue[activeColumnValue]['=']) {
      filterQueryValue[activeColumnValue] = {
        '=': {
          value: text,
        },
      }
    } else {
      filterQueryValue[activeColumnValue]['='].value = text
    }
    filterQuery.value = filterQueryValue

    const searchedValue = text
    relevantQuery[relevantColumn] = {
      '=': {
        value: searchedValue,
      },
    } as OperatorObject

    const AutoSearchAfterNChars = 3
    if (
      autoCompleteQuery.value &&
      autoCompleteQuery.value.length < AutoSearchAfterNChars &&
      searchedValue.length >= AutoSearchAfterNChars
    ) {
      // TODO: Needs different implementation
      // await search(
      //   {
      //     filters: {
      //       userFilters: relevantQuery,
      //       staticFilters: flattenedStaticFilters.value,
      //     },
      //   },
      //   {
      //     table: autoCompleteTable.autoCompleteTable,
      //   }
      // )
    }
    autoCompleteQuery.value = searchedValue
    calculateAutocomplete(searchedValue)
  }

  function updateQuery(query: OperatorObject, column: SearchQueryKeys<ITEM>) {
    const mergedQuery = {
      ...(filterQuery.value as SearchQuery<ITEM>),
    }

    // Remove the operators from the query if the value is empty
    const entries = Object.entries(query)
    let entriesLength = entries.length
    entries.forEach(([column, operatorList]) => {
      if (
        operatorList.value === undefined ||
        // operatorList.value === null ||
        operatorList.value === ''
      ) {
        delete query[column]
        entriesLength--
      }
    })
    if (entriesLength !== 0) {
      mergedQuery[column || activeColumn.value] = query
    } else {
      // If all the operators are empty, remove the column from the query
      delete mergedQuery[column || activeColumn.value]
    }

    filterQuery.value = mergedQuery
    if (options?.autoCompleteManager) {
      // Loop through the columns and check if the column is searchable
      Object.entries<SearchQuery<ITEM>[any]>(mergedQuery).forEach(([column, _operatorList]) => {
        const operatorList = _operatorList as OperatorObject
        if (
          !operatorList ||
          !('=' in operatorList) ||
          !operatorList['='].smartSearchEnabled ||
          typeof operatorList['='].value !== 'string'
        )
          return

        let schemaColumn: JSSchemaObject = store.getSchemaObjectFromFlattenedKeys(schema, column)

        let autoCompleteTable: TABLE = table
        let relevantColumn = column

        if ('relation' in schemaColumn && schemaColumn.relation) {
          autoCompleteTable = schemaColumn.relation?.split('.')[0] as TABLE

          // Use correct autocomplete table if the column has a relation to another table
          const periodIndex = schemaColumn.relation?.indexOf('.')
          relevantColumn =
            periodIndex && periodIndex !== -1
              ? schemaColumn.relation?.slice(periodIndex + 1)
              : column

          if ('foreignKey' in schemaColumn) {
            relevantColumn = schemaColumn.foreignKey
          }
          schemaColumn = store.getSchemaReferencedObject(schemaColumn)
        }

        const realSchema = schemaColumn as JSSchemaObjectTypes

        // If the column is not a string, we don't want to use the smart search
        if (realSchema.type != 'string') return

        const smartQuery =
          options?.autoCompleteManager
            .getColumn(AutoCompleteManager.formatKey(autoCompleteTable, relevantColumn))
            ?.getCorrectedSearch(operatorList['='].value, realSchema.smartSearchType) ||
          operatorList['='].value

        mergedQuery[column]['='].smartSearchValue = smartQuery
      })

      // Sets the autocomplete query to the value of the column if the column has an operator of '=', else it just resets the autocomplete query
      autoCompleteQuery.value = mergedQuery[column || activeColumn.value]?.['=']?.value || ''
    }

    updateParams(mergedQuery)
    search({
      filters: {
        userFilters: mergedQuery,
        staticFilters: flattenedStaticFilters.value,
      },
    })
  }

  function resetQuery() {
    filterQuery.value = {}
    updateParams({})
    search({
      filters: {},
    })
  }

  const activeColumn = ref<SearchQueryKeys<ITEM> | null>(
    searchableColumns?.length > 0 ? searchableColumns[0].key : null
  )

  function updateActiveColumn(column: string) {
    activeColumn.value = column

    const schemaValue = store.getSchemaObjectFromFlattenedKeys(schema, column)
    const colName =
      // @ts-ignore
      'relation' in schemaValue
        ? // @ts-ignore
          schemaValue.relation
        : AutoCompleteManager.formatKey(table, column)

    options?.autoCompleteManager?.addColumn(colName)
  }

  function removeColumn(column: SearchQueryKeys<ITEM>) {
    const newFilterQuery = { ...(filterQuery.value as SearchQuery<ITEM>) }
    delete newFilterQuery[column]
    updateQuery({}, column)
  }

  return {
    filterQuery,
    searchableColumns,
    autoComplete,
    autoCompleteLoading,
    activeColumn,
    staticValues: flattenedStaticFilters,
    updateQuery,
    updateAutoCompleteQuery,
    updateActiveColumn,
    removeColumn,
    resetQuery,
  }
}
